import styled from "styled-components";
import { toArray } from "../../api-utils/item-filters";
import { pxToRem } from "@input-output-hk/px-to-rem";
import colors from "../../tokens/colors";
import { forPhoneOnly } from "../../tokens/media-queries";
import { VisuallyHidden, visuallyHiddenCss } from "../a11y/VisuallyHidden";
import Facebook from "../icons/Facebook";
import LinkedIn from "../icons/LinkedIn";
import Share from "../icons/Share";
import Twitter from "../icons/Twitter";

const SocialIconsSet = styled.div`
  --social-icon-gap: 0;
  display: flex;
  flex-direction: row;
  color: var(--social-icon-stroke);
  align-items: center;
  margin: calc(var(--social-icon-gap) * -1);

  > * {
    margin: var(--social-icon-gap);
  }
`;
const ShareHeader = styled.div`
  color: var(--default-text-color-muted);

  @media ${forPhoneOnly} {
    ${visuallyHiddenCss}
  }
`;

const SocialLink = styled.a`
  display: inline-block;
  padding: 0.5rem;
  cursor: pointer;
  color: ${colors.primary.electricBlue};
  text-decoration: none;

  & > [data-icon] {
    width: ${pxToRem(22)};
    height: ${pxToRem(22)};
  }
`;

type ShareData = {
  text: string;
  url: string;
  title?: string;
  image?: string | string[];
};

function createTwitterUrl(data: ShareData) {
  const url = new URL("https://twitter.com/intent/tweet");
  url.searchParams.set("url", data.url);
  url.searchParams.set("text", data.text);
  url.searchParams.set("via", "cardano");
  url.searchParams.append("via", "InputOutputHK");
  return url.toString();
}

function createFacebookUrl(data: ShareData) {
  const url = new URL("https://www.facebook.com/share.php");
  url.searchParams.set("u", data.url);
  url.searchParams.set("t", data.text);
  return url.toString();
}

function createLinkedInUrl(data: ShareData) {
  const url = new URL("https://www.linkedin.com/sharing/share-offsite/");
  url.searchParams.set("url", data.url);
  return url.toString();
}

function imageUrlToFile(imageUrl: string) {
  return fetch(imageUrl)
    .then((res) => res.blob())
    .then((blob) => new File([blob], "image.png", { type: blob.type }));
}

interface TProps {
  shareData: ShareData;
}
const SocialIcons: React.FC<TProps> = ({ shareData }) => {
  const supportsNativeShare =
    typeof navigator !== "undefined" && "share" in navigator;
  const handleNativeShare = async () => {
    const files = shareData.image
      ? await Promise.all(
          toArray(shareData.image).map((i) => imageUrlToFile(i))
        )
      : undefined;
    try {
      await navigator.share({
        url: shareData.url,
        text: shareData.text,
        files
      });
    } catch (err) {
      console.error(err);
    }
  };
  return (
    <SocialIconsSet data-social-icon-set>
      <ShareHeader>Share:</ShareHeader>
      <SocialLink
        href={createTwitterUrl(shareData)}
        target="_blank"
        rel="noopener noreferrer nofollow"
      >
        <Twitter />
        <VisuallyHidden>Share this on Twitter</VisuallyHidden>
      </SocialLink>
      <SocialLink
        href={createFacebookUrl(shareData)}
        target="_blank"
        rel="noopener noreferrer nofollow"
      >
        <Facebook />
        <VisuallyHidden>Share this on Facebook</VisuallyHidden>
      </SocialLink>
      <SocialLink
        href={createLinkedInUrl(shareData)}
        target="_blank"
        rel="noopener noreferrer nofollow"
      >
        <LinkedIn />
        <VisuallyHidden>Share this on LinkedIn</VisuallyHidden>
      </SocialLink>
      {supportsNativeShare && (
        <SocialLink onClick={handleNativeShare}>
          <Share />
          <VisuallyHidden>Share this with...</VisuallyHidden>
        </SocialLink>
      )}
    </SocialIconsSet>
  );
};

export default SocialIcons;
